import { c as _c } from "react/compiler-runtime";
import { IcoArrowUpRight16, IcoInfo16 } from '@onefootprint/icons';
import type { EntityOnboarding } from '@onefootprint/request-types/dashboard';
import { CodeInline, IconButton, LinkButton, Popover } from '@onefootprint/ui';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { onboardingHasPlaybook } from '../../../../../onboardings-shared/utils';
import BusinessLink from './business-link';
type OnboardingDetailsProps = {
  onboarding: EntityOnboarding;
  formatTimestamp: (timestamp: string) => string;
};

/** The onboarding's ids and links, behind one button so the header stays readable. */
const OnboardingDetails = t0 => {
  const $ = _c(52);
  const {
    onboarding,
    formatTimestamp
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "onboardings.details"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t1);
  const playbook = onboardingHasPlaybook(onboarding) ? onboarding.playbook : undefined;
  const [isOpen, setIsOpen] = useState(false);
  let t2;
  if ($[1] !== t) {
    t2 = t(_temp);
    $[1] = t;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== onboarding.id) {
    t3 = <CodeInline size="compact">{onboarding.id}</CodeInline>;
    $[3] = onboarding.id;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = {
      label: t2,
      value: t3
    };
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== onboarding.externalId || $[9] !== t) {
    t5 = onboarding.externalId ? [{
      label: t(_temp2),
      value: <CodeInline size="compact">{onboarding.externalId}</CodeInline>
    }] : [];
    $[8] = onboarding.externalId;
    $[9] = t;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== playbook || $[12] !== t) {
    t6 = playbook ? [{
      label: t(_temp3),
      value: <LinkButton href={playbook.isHidden ? `/playbooks/${playbook.playbookId}` : `/playbooks/${playbook.playbookId}?${new URLSearchParams({
        version: playbook.id
      })}`} target="_blank" variant="label-3" iconComponent={IcoArrowUpRight16} className="underline! underline-offset-2 [&_svg]:size-3.5">{playbook.isHidden ? t(_temp4, {
          name: playbook.name
        }) : t(_temp5, {
          name: playbook.name,
          version: playbook.version
        })}</LinkButton>
    }] : [];
    $[11] = playbook;
    $[12] = t;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t) {
    t7 = t(_temp6);
    $[14] = t;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== formatTimestamp || $[17] !== onboarding.timestamp) {
    t8 = formatTimestamp(onboarding.timestamp);
    $[16] = formatTimestamp;
    $[17] = onboarding.timestamp;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t7 || $[20] !== t8) {
    t9 = {
      label: t7,
      value: t8
    };
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== formatTimestamp || $[23] !== onboarding.completedAt || $[24] !== t) {
    t10 = onboarding.completedAt ? [{
      label: t(_temp7),
      value: formatTimestamp(onboarding.completedAt)
    }] : [];
    $[22] = formatTimestamp;
    $[23] = onboarding.completedAt;
    $[24] = t;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== onboarding.businessInfo || $[27] !== t) {
    t11 = onboarding.businessInfo ? [{
      label: t(_temp8),
      value: <BusinessLink businessInfo={onboarding.businessInfo} />
    }] : [];
    $[26] = onboarding.businessInfo;
    $[27] = t;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== t10 || $[30] !== t11 || $[31] !== t4 || $[32] !== t5 || $[33] !== t6 || $[34] !== t9) {
    t12 = [t4, ...t5, ...t6, t9, ...t10, ...t11];
    $[29] = t10;
    $[30] = t11;
    $[31] = t4;
    $[32] = t5;
    $[33] = t6;
    $[34] = t9;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  const rows = t12;
  let t13;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => setIsOpen(false);
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== t) {
    t14 = t(_temp10);
    $[37] = t;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== rows) {
    t15 = <dl className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-4 gap-y-3">{rows.map(_temp11)}</dl>;
    $[39] = rows;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== t14 || $[42] !== t15) {
    t16 = <div role="dialog" aria-label={t14} className="w-max max-w-[calc(100vw-2rem)] p-4">{t15}</div>;
    $[41] = t14;
    $[42] = t15;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  if ($[44] !== t) {
    t17 = t(_temp12);
    $[44] = t;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== t17) {
    t18 = <IconButton aria-label={t17} icon={IcoInfo16} variant="outline" size="compact" />;
    $[46] = t17;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  let t19;
  if ($[48] !== isOpen || $[49] !== t16 || $[50] !== t18) {
    t19 = <Popover side="bottom" align="end" controlledOpen={isOpen} onControlledOpenChange={setIsOpen} onEscapeKeyDown={t13} className="rounded-default!" onOpenAutoFocus={_temp9} content={t16}>{t18}</Popover>;
    $[48] = isOpen;
    $[49] = t16;
    $[50] = t18;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  return t19;
};
export default OnboardingDetails;
function _temp($) {
  return $.onboardingId;
}
function _temp2($_0) {
  return $_0.externalId;
}
function _temp3($_1) {
  return $_1.playbook;
}
function _temp4($_2) {
  return $_2.unsavedVersion;
}
function _temp5($_3) {
  return $_3.version;
}
function _temp6($_4) {
  return $_4.started;
}
function _temp7($_5) {
  return $_5.completed;
}
function _temp8($_6) {
  return $_6.business;
}
function _temp9(event) {
  event.preventDefault();
  (event.currentTarget as HTMLElement | null)?.focus();
}
function _temp10($_7) {
  return $_7.ariaLabel;
}
function _temp11(t0) {
  const {
    label,
    value
  } = t0;
  return <div key={label} className="contents"><dt className="text-body-3 text-tertiary">{label}</dt><dd className="min-w-0 text-body-3 text-primary">{value}</dd></div>;
}
function _temp12($_8) {
  return $_8.ariaLabel;
}